import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CompactAssistant } from './DashboardAssistantChatDock/components/CompactAssistant';
import { Sparkles } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useDashboardAssistantDock } from '../../contexts/DashboardAssistantDockContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ChatPanelFrame } from './DashboardAssistantChatDock/types';
import {
  chatPanelLayoutTransition,
  dropdownMotion,
  getCompactChatFrame,
  getDockedChatFrame,
  getMaximizedChatFrame,
} from './DashboardAssistantChatDock/lib/panelLayout';
import { DashboardAssistantChatSessionPanel } from './DashboardAssistantChatDock/components/ChatSessionPanel';
import { DashboardAssistantChatHistoryMenu } from './DashboardAssistantChatDock/components/ChatHistoryMenu';
import { SessionChip } from './DashboardAssistantChatDock/components/SessionChip';

const AskCharityStackTooltip = () => (
  <div className="flex items-center gap-3">
    <span>Ask CharityStack AI</span>
    <span className="flex items-center gap-0.5 rounded border border-edge bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-ink">
      <span aria-hidden="true">⌘</span>
      <span>J</span>
    </span>
  </div>
);

const AssistantChatSplitButton = ({ onOpenNewChat }: { onOpenNewChat: () => void }) => (
  <div className="flex h-8 shrink-0 items-center rounded-lg border border-edge/80 bg-white text-ink shadow-sm">
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onOpenNewChat}
          className="flex h-full items-center gap-1.5 rounded-l-lg px-3 text-xs font-medium transition hover:bg-paper-light focus:outline-none focus:ring-0 focus-visible:ring-0"
          type="button"
          aria-label="Ask CharityStack AI"
        >
          <Sparkles className="h-3.5 w-3.5 text-ink-muted" />
          Ask CharityStack AI
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <AskCharityStackTooltip />
      </TooltipContent>
    </Tooltip>

    <DashboardAssistantChatHistoryMenu
      rootClassName="relative flex h-full"
      triggerClassName="flex h-full w-9 items-center justify-center rounded-r-lg border-l border-edge/80 text-ink transition hover:bg-paper-light focus:outline-none focus:ring-0 focus-visible:ring-0"
      iconClassName="h-3.5 w-3.5 text-ink-muted"
    />
  </div>
);

function isSameChatPanelFrame(
  currentFrame: ChatPanelFrame | null,
  nextFrame: ChatPanelFrame,
) {
  if (!currentFrame) {
    return false;
  }

  return (
    currentFrame.height === nextFrame.height &&
    currentFrame.left === nextFrame.left &&
    currentFrame.top === nextFrame.top &&
    currentFrame.width === nextFrame.width
  );
}

export function DashboardAssistantChatDock({
  dashboardTimezone,
  merchantCountryCode,
  onCompactOpen,
}: {
  dashboardTimezone?: string | null;
  merchantCountryCode?: string | null;
  onCompactOpen?: () => void;
}) {
  const {
    assistance,
    resumeAssistant,
    activeSessionId,
    closeSession,
    isDocked,
    isExpanded,
    minimize,
    openAssistantOrResume,
    openSession,
    sessions,
    toggleDocked,
  } = useDashboardAssistantDock();

  const dockRef = useRef<HTMLDivElement | null>(null);
  const sessionStripRef = useRef<HTMLDivElement | null>(null);
  const activeChipRef = useRef<HTMLDivElement | null>(null);
  const [chatPanelFrame, setChatPanelFrame] = useState<ChatPanelFrame | null>(null);
  const [maximizedSessionId, setMaximizedSessionId] = useState<string | null>(null);
  const isDesktopViewport = !assistance.isCompact;
  const [sessionStripFade, setSessionStripFade] = useState({
    left: false,
    right: false,
  });
  const shouldReduceMotion = useReducedMotion();
  const openSessions = useMemo(() => sessions.filter((session) => !session.isClosed), [sessions]);

  const activeSession = openSessions.find((session) => session.id === activeSessionId) || null;
  const isChatMaximized =
    isExpanded && activeSession !== null && maximizedSessionId === activeSession.id;
  const isChatDocked = isDocked && isDesktopViewport;

  const updateSessionStripFade = useCallback(() => {
    const strip = sessionStripRef.current;
    if (!strip) {
      setSessionStripFade((current) =>
        current.left || current.right ? { left: false, right: false } : current,
      );
      return;
    }

    const maxScrollLeft = strip.scrollWidth - strip.clientWidth;
    const canScroll = maxScrollLeft > 1;
    const nextFade = {
      left: canScroll && strip.scrollLeft > 2,
      right: canScroll && strip.scrollLeft < maxScrollLeft - 2,
    };

    setSessionStripFade((current) =>
      current.left === nextFade.left && current.right === nextFade.right ? current : nextFade,
    );
  }, []);

  const handleToggleMaximized = () => {
    if (!activeSession) {
      return;
    }

    setMaximizedSessionId((currentSessionId) =>
      currentSessionId === activeSession.id ? null : activeSession.id,
    );
  };

  useEffect(() => {
    if (isChatDocked) {
      setMaximizedSessionId(null);
    }
  }, [isChatDocked]);

  useLayoutEffect(() => {
    if (!isExpanded || !activeSession) {
      setChatPanelFrame(null);
      return undefined;
    }

    const updatePanelFrame = () => {
      const nextFrame = isChatDocked
        ? getDockedChatFrame()
        : isChatMaximized
          ? getMaximizedChatFrame()
          : getCompactChatFrame(activeChipRef.current);
      setChatPanelFrame((currentFrame) =>
        isSameChatPanelFrame(currentFrame, nextFrame) ? currentFrame : nextFrame,
      );
    };

    updatePanelFrame();
    window.addEventListener('resize', updatePanelFrame);

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePanelFrame) : null;
    const sessionStrip = sessionStripRef.current;
    if (observer) {
      if (dockRef.current) {
        observer.observe(dockRef.current);
      }
      if (activeChipRef.current) {
        observer.observe(activeChipRef.current);
      }
    }
    sessionStrip?.addEventListener('scroll', updatePanelFrame, { passive: true });

    return () => {
      window.removeEventListener('resize', updatePanelFrame);
      sessionStrip?.removeEventListener('scroll', updatePanelFrame);
      observer?.disconnect();
    };
  }, [activeSession, isChatDocked, isChatMaximized, isExpanded, openSessions.length]);

  useLayoutEffect(() => {
    updateSessionStripFade();

    const strip = sessionStripRef.current;
    if (!strip) {
      return undefined;
    }

    const handleStripUpdate = () => updateSessionStripFade();
    const animationFrameId = window.requestAnimationFrame(updateSessionStripFade);

    strip.addEventListener('scroll', handleStripUpdate, { passive: true });
    window.addEventListener('resize', handleStripUpdate);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleStripUpdate) : null;
    resizeObserver?.observe(strip);
    Array.from(strip.children).forEach((child) => resizeObserver?.observe(child));

    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(handleStripUpdate) : null;
    mutationObserver?.observe(strip, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      strip.removeEventListener('scroll', handleStripUpdate);
      window.removeEventListener('resize', handleStripUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [openSessions, updateSessionStripFade]);

  useLayoutEffect(() => {
    activeChipRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });

    const animationFrameId = window.requestAnimationFrame(updateSessionStripFade);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [activeSessionId, openSessions.length, updateSessionStripFade]);

  useEffect(() => {
    if (!isExpanded || !activeSession || maximizedSessionId === activeSession.id) {
      return;
    }

    setMaximizedSessionId(null);
  }, [activeSession, isExpanded, maximizedSessionId]);

  useEffect(() => {
    if (!isDesktopViewport || !isExpanded || !activeSession) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      minimize();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeSession, isDesktopViewport, isExpanded, minimize]);

  if (!isDesktopViewport) {
    return <>
      <button type="button" aria-label="Ask CharityStack AI" onClick={() => { onCompactOpen?.(); resumeAssistant(); }} className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-edge bg-paper-light text-ink shadow-sm transition-colors hover:bg-paper-inset focus-visible:outline focus-visible:outline-2 focus-visible:outline-sage">
        <Sparkles className="h-6 w-6" aria-hidden="true" />
      </button>
      <CompactAssistant dashboardTimezone={dashboardTimezone} merchantCountryCode={merchantCountryCode} />
    </>;
  }

  if (openSessions.length === 0) {
    return (
      <div className="flex min-w-0 flex-1 justify-end">
        <AssistantChatSplitButton onOpenNewChat={openAssistantOrResume} />
      </div>
    );
  }

  return (
    <div className="relative flex min-w-0 flex-1 flex-col items-end" ref={dockRef}>
      <AnimatePresence>
        {isExpanded && activeSession ? (
          <motion.div
            key={`assistant-chat-${activeSession.id}`}
            initial={{
              opacity: 0,
              scale: 0.97,
              y: 10,
              ...(chatPanelFrame ||
                (isChatDocked
                  ? getDockedChatFrame()
                  : getCompactChatFrame(activeChipRef.current))),
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              ...(chatPanelFrame ||
                (isChatDocked
                  ? getDockedChatFrame()
                  : isChatMaximized
                    ? getMaximizedChatFrame()
                    : getCompactChatFrame(activeChipRef.current))),
            }}
            exit={{
              opacity: 0,
              scale: 0.97,
              y: 10,
              transition: dropdownMotion.transition,
            }}
            transition={
              shouldReduceMotion ? { duration: 0 } : chatPanelLayoutTransition
            }
            className="fixed z-[95] origin-bottom-right bg-paper-muted"
            style={{
              pointerEvents: chatPanelFrame ? 'auto' : 'none',
            }}
          >
            <DashboardAssistantChatSessionPanel
              dashboardTimezone={dashboardTimezone}
              merchantCountryCode={merchantCountryCode}
              isDocked={isChatDocked}
              isMaximized={isChatMaximized}
              onToggleDocked={toggleDocked}
              onToggleMaximized={handleToggleMaximized}
              session={activeSession}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="flex w-full max-w-full min-w-0 items-center justify-end gap-2">
        <div className="relative min-w-0 flex-1">
          <div
            ref={sessionStripRef}
            className="min-w-0 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex w-max min-w-full items-center justify-end gap-2">
              {openSessions.map((session) => (
                <SessionChip
                  key={session.id}
                  chipRef={session.id === activeSessionId ? activeChipRef : undefined}
                  session={session}
                  isActive={session.id === activeSessionId && isExpanded}
                  onOpen={() => openSession(session.id)}
                  onClose={() => closeSession(session.id)}
                />
              ))}
            </div>
          </div>

          <div
            className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#F5F3ED] via-[#F5F3ED]/80 to-transparent backdrop-blur-[1px] transition-opacity duration-150 [mask-image:linear-gradient(to_right,black_0%,black_40%,transparent_100%)] ${
              sessionStripFade.left ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div
            className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#F5F3ED] via-[#F5F3ED]/80 to-transparent backdrop-blur-[1px] transition-opacity duration-150 [mask-image:linear-gradient(to_left,black_0%,black_40%,transparent_100%)] ${
              sessionStripFade.right ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        <div className="h-7 w-px shrink-0 bg-edge" aria-hidden="true" />

        <AssistantChatSplitButton onOpenNewChat={openAssistantOrResume} />
      </div>
    </div>
  );
}

export default DashboardAssistantChatDock;

// Re-exports preserved for the message-model test suite (see __tests__/DashboardAssistantChatDock.model.test.ts).
// The implementations live in ./DashboardAssistantChatDock/lib/messageModel.
export {
  getAgentMessageSyncKey,
  getMessageDedupeKey,
  getRuntimeMessageSyncKeys,
  getDockMessageSyncKeys,
  mergeDockMessages,
  mergePersistedHistoryWithLiveMessages,
  normalizeMessageTurnOrder,
  sanitizeAssistantVisibleContent,
  sortMessagesByCanonicalOrder,
} from './DashboardAssistantChatDock/lib/messageModel';
